import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, List, Paper, Typography } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import TicketListItemCustom from "../TicketListItemCustom";

const MAX_AUTO_FILL_PAGES = 3;

const elapsed = date => {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const NoReplyTicketsList = ({ preferences, setTabOpen, onCount }) => {
  const { socket, user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingRef = useRef(false);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(async (requestedPage = 1, generation = generationRef.current) => {
    if (!preferences || generation !== generationRef.current || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      let nextPage = requestedPage;
      while (true) {
        const loadedPage = nextPage;
        const { data } = await api.get("/no-reply", { params: { ...preferences, pageNumber: loadedPage } });
        if (!mountedRef.current || generation !== generationRef.current) return;
        setTickets(current => {
          if (loadedPage === 1) return data.tickets;
          const seen = new Set(current.map(item => `${item.id}-${item.noReply.anchorMessageId}`));
          return [...current, ...data.tickets.filter(item => !seen.has(`${item.id}-${item.noReply.anchorMessageId}`))];
        });
        pageRef.current = loadedPage;
        hasMoreRef.current = Boolean(data.hasMore);
        setPage(loadedPage);
        setHasMore(data.hasMore);
        onCount(data.count);

        const root = scrollRef.current;
        const scrollHeight = Number(root?.scrollHeight);
        const clientHeight = Number(root?.clientHeight);
        const cannotMeasure = !Number.isFinite(scrollHeight) || !Number.isFinite(clientHeight);
        const shouldFillMore = Boolean(data.hasMore)
          && data.tickets.length > 0
          && root
          && loadedPage - requestedPage + 1 < MAX_AUTO_FILL_PAGES
          && (cannotMeasure || scrollHeight <= clientHeight + 240);
        if (!shouldFillMore) break;
        nextPage += 1;
      }
    } catch (err) {
      if (mountedRef.current && generation === generationRef.current) toastError(err);
    } finally {
      if (mountedRef.current && generation === generationRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [preferences, onCount]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    loadingRef.current = false;
    pageRef.current = 1;
    hasMoreRef.current = false;
    setTickets([]);
    setPage(1);
    setHasMore(false);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    load(1, generation);
  }, [load]);
  useEffect(() => {
    if (!socket) return undefined;
    const reload = () => load(1);
    socket.on(`company-${user.companyId}-noreply`, reload);
    return () => socket.off(`company-${user.companyId}-noreply`, reload);
  }, [socket, user.companyId, load]);
  useEffect(() => {
    if (!hasMore || loading || !scrollRef.current || !loadMoreRef.current) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) load(page + 1);
    }, { root: scrollRef.current, rootMargin: "240px 0px", threshold: 0.01 });
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [tickets.length, hasMore, loading, load, page]);
  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const root = scrollRef.current;
    const scrollHeight = Number(root?.scrollHeight);
    const clientHeight = Number(root?.clientHeight);
    const cannotMeasure = !Number.isFinite(scrollHeight) || !Number.isFinite(clientHeight);
    if (mountedRef.current && root && (cannotMeasure || scrollHeight <= clientHeight + 240)) {
      load(pageRef.current + 1);
    }
    return undefined;
  }, [tickets.length, hasMore, loading, load]);

  const handleScroll = event => {
    const root = event.currentTarget;
    const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;
    if (remaining < 240 && hasMoreRef.current && !loadingRef.current) load(pageRef.current + 1);
  };

  const dismiss = async (event, ticket) => {
    event.stopPropagation();
    try {
      await api.post(`/no-reply/${ticket.id}/dismiss`, {
        anchorMessageId: ticket.noReply.anchorMessageId,
        version: ticket.noReply.version
      });
      setTickets(current => current.filter(item => item.id !== ticket.id));
      onCount(current => Math.max(0, Number(current) - 1));
    } catch (err) {
      toastError(err);
      load(1);
    }
  };

  return (
    <Paper
      ref={scrollRef}
      onScroll={handleScroll}
      style={{ flex: 1, minHeight: 0, width: "100%", overflowY: "auto", borderTop: "2px solid rgba(0,0,0,.12)" }}
    >
      <List disablePadding>
        {tickets.map(ticket => (
          <div key={`${ticket.id}-${ticket.noReply.anchorMessageId}`} style={{ borderBottom: "1px solid rgba(40, 100, 165, 0.18)" }}>
            <div style={{ padding: "2px 7px", minHeight: 25, background: "#fff8e1", display: "flex", gap: 5, alignItems: "center" }}>
              <AccessTimeIcon style={{ fontSize: 14, color: "#ef6c00" }} />
              <Typography variant="caption" noWrap style={{ minWidth: 0, flex: 1, color: "#6d4c10" }}>
                <strong>Sem resposta há {elapsed(ticket.noReply.anchorSentAt)}{preferences.skipWeekends ? " corridos" : ""}</strong>
              </Typography>
              <Button size="small" onClick={event => dismiss(event, ticket)} style={{ textTransform: "none", fontSize: 10, padding: "1px 4px", minWidth: 0 }}>
                Dispensar
              </Button>
            </div>
            <TicketListItemCustom ticket={ticket} setTabOpen={setTabOpen} myAttendances={preferences.onlyMine} />
          </div>
        ))}
      </List>
      {!loading && tickets.length === 0 && (
        <Typography align="center" color="textSecondary" style={{ padding: 32 }}>Nenhum atendimento sem retorno.</Typography>
      )}
      {loading && tickets.length === 0 && <div style={{ textAlign: "center", padding: 18 }}><CircularProgress size={24} /></div>}
      {hasMore && <div ref={loadMoreRef} style={{ textAlign: "center", minHeight: 34, padding: 6 }}>{loading ? <CircularProgress size={20} /> : <Button size="small" onClick={() => load(page + 1)}>Carregar mais</Button>}</div>}
    </Paper>
  );
};

export default NoReplyTicketsList;
